import { Archive, ArchiveRestore, Plus, Search } from "lucide-react";
import { useId, useState } from "react";

import type { ProjectListItem } from "@/api/hooks";
import { queryKeys, useProjectsQuery } from "@/api/hooks";
import {
  ProjectLifecycleDialog,
  type ProjectLifecycleMode,
} from "@/ui/project/ProjectLifecycleDialog";
import { QueryErrorState } from "@/api/QueryErrorState";
import { QueryPendingState } from "@/api/QueryPendingState";
import { isTerminalQueryError } from "@/api/queryClient";
import { useCapability } from "@/auth/SessionContext";
import { PageHeader } from "@/shell/PageHeader";
import { publicationBadgeLabel, readinessLabel } from "@/lib/projectStatus";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Card } from "@/ui/Card";
import { DataRow } from "@/ui/DataRow";
import { IconButton } from "@/ui/IconButton";
import { Input } from "@/ui/Input";
import { Tooltip } from "@/ui/Tooltip";
import { EmptyState, StaleDataNotice } from "@/ui/states";

import { NewProjectDialog } from "./NewProjectDialog";

/**
 * ONE column track, at every width (#439).
 *
 * The canonical reference declares `2fr 1fr 1.5fr auto` with a 36px action
 * cell, and keeps it at 390 with the header row visible: no stacked variant, no
 * `md:` breakpoint, and therefore no second copy of a row in the DOM for
 * assistive technology to walk twice. Measured on the reference at 1280 —
 * 495.109 / 247.547 / 371.344 / 36 — and this declaration computes the same
 * three numbers there.
 *
 * `minmax(0, …)` rather than the bare `2fr`, which is the one deliberate
 * departure. An `fr` track's automatic minimum is its content, so at 390 the
 * reference's own rows disagree with their own header by ~16px and a single
 * unbroken project name would push the grid past the card. Flooring each track
 * at zero keeps every row aligned to the header the page already draws and
 * makes overflow unrepresentable rather than merely unobserved; the ratio, the
 * fixed action column and the shared track are unchanged.
 */
const COLUMNS = "grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.5fr)_36px]";

/*
 * The per-row lifecycle control (#440, over #434's service half), and what
 * gates it: the CALLER's capability alone — `project.archive` for an active
 * row, `project.restore` (admin-alone, the ratified contract's §2.1) for an
 * archived one. Archive stays disabled-with-reason when capability is absent;
 * Restore is different by #440's explicit boundary: ordinary clinicians and
 * project editors must not SEE it, so an archived row has no lifecycle control
 * unless the caller actually holds `project.restore`. There is deliberately
 * no pre-flight eligibility verdict to render: `ProjectArchiveStatus` carries
 * facts, and the one state-refusal the service raises
 * (`publication_in_flight`) is answered by the operation itself with a stable
 * reason, surfaced in the confirmation dialog — #650's capability contract
 * owns folding that verdict into the read models, and deriving one here from
 * readiness or publication state would be exactly the client-only archive
 * flag this ticket names a non-goal.
 *
 * Disabled-with-reason, never absent (`Button`/`IconButton` express it as
 * `aria-disabled` so the element keeps its tab-order place and the reason
 * stays hover- and keyboard-reachable).
 */

function ColumnHeader() {
  return (
    <div
      className={`grid ${COLUMNS} items-center gap-3 border-b border-border px-3.5 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground`}
    >
      {/*
        Four columns, and only these four (#439's canonical convergence
        amendment). `Base pack` is gone with every rendering of
        `baseContentSha256`: a 12-character content digest occupied the space a
        clinician reads for clinical status, which is what the amendment
        replaces it with. The digest itself is untouched on the API.

        The header stays MOUNTED at every width. It used to carry
        `max-md:hidden`, so a narrow screen showed cells with nothing naming
        them; the canonical reference keeps it visible at 390 and so does this.
      */}
      <span>Project</span>
      <span>Status</span>
      <span>Preview target</span>
      {/*
        Unlabelled on the canonical surface, which draws an empty cell here.
        Named for assistive technology only, as `LibraryCatalog` does: a column
        of controls with no header is a column a screen-reader user meets
        without warning.
      */}
      <span className="sr-only">Actions</span>
    </div>
  );
}

function ProjectRow({
  project,
  archiveRefusal,
  restoreAllowed,
  onLifecycle,
}: {
  project: ProjectListItem;
  /** Why this caller cannot archive, or `null` when they can. */
  archiveRefusal: string | null;
  /** Restore is rendered only for the admin/operations capability. */
  restoreAllowed: boolean;
  onLifecycle: (project: ProjectListItem, mode: ProjectLifecycleMode) => void;
}) {
  /*
    BOTH badges are read straight off the projection and neither is composed.
    `readiness` is the rung the authoring has reached and `publicationStatus`
    is #267's compact clinician status — the same value the Project page shows,
    which is why `lib/projectStatus` holds one table for it rather than two.
    The second badge is omitted entirely on `none`; the label module says why.

    What is NOT here is as much of the point as what is: no `attentionReason`,
    no `nextAction`, no `currentRevision`, no `lineage.sourceVersion`, no
    `lastMeaningfulActivityAt`. They all arrive on the wire and this surface
    renders none of them.
  */
  const publicationBadge = publicationBadgeLabel(
    project.publicationStatus.state,
  );

  /*
    The lineage note the canonical surface puts under a copied Project's name,
    and only there. `origin` decides — never the presence of a name — so a
    seeded Project whose `sourceName` is its base pack does not acquire a note
    claiming it was copied from one. The snapshot wording is the contract's own
    reading of the field: the source name is "an IMMUTABLE SNAPSHOT taken when
    the Project was created … deliberately not re-resolved".
  */
  const copiedFrom =
    project.lineage.origin === "copied_project" && project.lineage.sourceName
      ? `Copied from ${project.lineage.sourceName} · starting snapshot`
      : null;

  /*
    The lifecycle control's direction comes from the row's own archive FACTS
    (#434's `archive.archived`), and the refusal from the caller's capability
    for that direction — see the module comment above for why there is no
    per-project eligibility verdict to consult. `archive` is optional in the
    generated type; an absent status reads as active, but its action fails
    closed with a reload reason before the dialog rather than defaulting a CAS
    revision the service never sent.
  */
  const archived = project.archive?.archived === true;
  const mode: ProjectLifecycleMode = archived ? "restore" : "archive";
  const refusal =
    project.archive?.archiveRevision === undefined
      ? "The Editor Service did not report this project's archive revision. Reload before changing its archive state."
      : archived
        ? null
        : archiveRefusal;
  const actionLabel = archived
    ? `Restore project ${project.name}`
    : `Archive project ${project.name}`;

  return (
    <DataRow
      to={`/projects/${encodeURIComponent(project.id)}`}
      title={project.name}
      {...(copiedFrom ? { subtitle: copiedFrom } : {})}
      className={`grid ${COLUMNS} items-center gap-3 px-3.5 py-3`}
      columns={
        // `display: contents` promotes these two cells into the row's own grid,
        // so one copy of each cell serves every width. A responsive variant
        // would mean two copies in the DOM and two announcements for one row.
        <div className="contents">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge tone="neutral">{readinessLabel(project.readiness)}</Badge>
            {publicationBadge ? (
              <Badge tone="outline">{publicationBadge}</Badge>
            ) : null}
            {/*
              In the badge cluster, not a fifth column: the four-track grid is
              the canonical reference's, and `AssetStateBadges` set the
              precedent that archived state rides beside the other flags.
            */}
            {archived ? <Badge tone="outline">Archived</Badge> : null}
          </div>
          <span className="truncate text-sm text-muted-foreground">
            {/*
              The id, verbatim. `/v1/preview-targets` returns bare ids and
              `ProjectListItem` carries no display name, so there is no
              organisation name to render and none is invented here — ratified
              tenant-neutrality keeps real customer names out of active-surface
              code. The canonical `Preview target required` state is likewise
              absent: `previewTenantId` is required with `minLength: 1`, so the
              contract cannot express a Project without one.
            */}
            {project.previewTenantId}
          </span>
        </div>
      }
      actions={
        archived && !restoreAllowed ? null : (
          // Painted above the row's stretched navigation link by `DataRow`, so
          // the browser hit-tests the control and never the link underneath it.
          // The tooltip carries the refusal when there is one and the action's
          // own name when there is not, `LibraryCatalog`'s shape.
          <Tooltip content={refusal ?? actionLabel}>
            <IconButton
              label={actionLabel}
              showTooltip={false}
              disabled={refusal !== null}
              icon={
                archived ? (
                  <ArchiveRestore className="h-4 w-4" />
                ) : (
                  <Archive className="h-4 w-4" />
                )
              }
              onClick={() => {
                if (refusal === null) {
                  onLifecycle(project, mode);
                }
              }}
            />
          </Tooltip>
        )
      }
    />
  );
}

/**
 * The landing view: every Project, its status, and what its previews run
 * against.
 *
 * Converged on the canonical reference by #439's 2026-08-25 amendment, which
 * replaced this route's scope outright. What left with it was the actionable
 * queue the ticket originally described — the `Needs attention` adornment, the
 * base-pack digest column and the `basedOn` subtitle — and the amendment is
 * explicit that the readiness and source fields behind them stay on the API,
 * unremoved, with no user-facing surface here.
 *
 * Every authorized Mentaily user sees this same list: #202 PR B2 collapsed
 * project scoping onto the deployment's environment, so there is no per-user
 * slice of it any more.
 */
export function ProjectsIndexRoute() {
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [lifecycle, setLifecycle] = useState<{
    project: ProjectListItem;
    mode: ProjectLifecycleMode;
  } | null>(null);
  const searchInputId = useId();
  const projects = useProjectsQuery(search, includeArchived);
  const createProject = useCapability("project.create");
  const archiveProject = useCapability("project.archive");
  const restoreProject = useCapability("project.restore");

  return (
    <>
      <PageHeader
        title="Projects"
        // Canonical, verbatim. This read "the base pack it pins" while the
        // route rendered a digest column; #202 PR B2's boundary — that what a
        // Project RUNS is a deployment fact this service is not the authority
        // for — governs what may be PROJECTED as a field, not the wording of a
        // page description, and #439 restores the canonical sentence.
        description="The landing view. All projects, what they run, what needs attention."
        actions={
          // #567: gated on the capability the CREATE itself requires, so a
          // subject without `project.create` never reaches a dialog whose
          // submit the server would refuse anyway. Disabled-with-reason, not
          // hidden — the server still enforces; this mirrors its decision.
          <Button
            disabled={!createProject.allowed}
            title={createProject.reason ?? undefined}
            onClick={() => {
              if (createProject.allowed) setCreating(true);
            }}
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            New project
          </Button>
        }
      />

      {/* Mounted only while open, so each creation starts from a clean form. */}
      {creating ? (
        <NewProjectDialog onClose={() => setCreating(false)} />
      ) : null}

      {/* Mounted only while open, so each decision reads a fresh revision. */}
      {lifecycle ? (
        <ProjectLifecycleDialog
          project={lifecycle.project}
          mode={lifecycle.mode}
          onClose={() => setLifecycle(null)}
        />
      ) : null}

      <div className="space-y-4">
        <label htmlFor={searchInputId} className="sr-only">
          Search projects
        </label>
        <div className="flex items-center gap-2">
          <Input
            id={searchInputId}
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search projects"
            leadingIcon={<Search className="h-4 w-4" />}
            className="flex-1"
          />
          {/*
            `LibraryCatalog`'s toggle, for the same reason it exists there:
            archived Projects are hidden by default so the index cannot offer
            them (#434's listing rule), and looking at them is an explicit
            act — soft state, not secrecy. `aria-pressed` because it is a
            toggle, not a navigation.
          */}
          <Button
            variant="outline"
            aria-pressed={includeArchived}
            onClick={() => setIncludeArchived((current) => !current)}
          >
            Show archived
          </Button>
        </div>

        {/*
          A transient failed *refresh* over a list already in the cache is not
          the same failure as a failed first load. Discarding the list costs the
          clinician the page; showing it without saying so lets them act on data
          that is older than it looks. The live region stays mounted so
          assistive technology observes the notice when it becomes visible.
        */}
        {projects.data ? (
          <StaleDataNotice
            updatedAt={projects.dataUpdatedAt}
            onRetry={() => void projects.refetch()}
            visible={
              projects.isError && !isTerminalQueryError(projects.error)
            }
          />
        ) : null}

        {projects.isPending ? (
          <QueryPendingState
            isPaused={projects.isPaused}
            failureReason={projects.failureReason}
            label="Loading projects"
            errorTitle="Projects could not be loaded"
            rows={4}
            queryKey={queryKeys.projects(search, includeArchived)}
          />
        ) : projects.isError &&
          (!projects.data || isTerminalQueryError(projects.error)) ? (
          <QueryErrorState
            error={projects.error}
            subject="Projects"
            title="Projects could not be loaded"
            description="The Editor Service did not return the project list."
            deniedDescription="You are not authorized to list projects in this environment."
            onRetry={() => void projects.refetch()}
          />
        ) : projects.data.items.length === 0 && !search ? (
          <EmptyState
            title={includeArchived ? "No projects yet" : "No active projects"}
            description={
              includeArchived
                ? "Projects you are authorized to open will appear here."
                : "Active projects you are authorized to open will appear here. Use Show archived to view retained projects."
            }
          />
        ) : (
          <Card className="overflow-hidden">
            <ColumnHeader />
            {projects.data.items.length === 0 ? (
              /*
                NO MATCH, and the table stays. The canonical surface keeps the
                column header above this sentence rather than replacing the
                whole table with a panel, because the search that produced it is
                still on screen and the columns are what the next keystroke will
                fill. An empty DATABASE is a different answer and keeps its own
                panel above.
              */
              <div className="px-3.5 py-8 text-center text-sm text-muted-foreground">
                No projects match your search.
              </div>
            ) : (
              projects.data.items.map((project) => (
                <ProjectRow
                  key={project.id}
                  project={project}
                  archiveRefusal={
                    archiveProject.allowed ? null : archiveProject.reason
                  }
                  restoreAllowed={restoreProject.allowed}
                  onLifecycle={(target, mode) =>
                    setLifecycle({ project: target, mode })
                  }
                />
              ))
            )}
          </Card>
        )}
      </div>
    </>
  );
}
